import { z } from 'zod';
import { HexColourSchema, I18nStringSchema, LocaleSchema } from './primitives.js';

/**
 * Tenant branding — drives the **white-label customer PWA**.
 * Per ADR-0010, every brand-touching value in apps/customer-pwa comes from here.
 */

export const AppIdentitySchema = z.object({
  name: z.string().min(1).max(40).describe('Full app name (home-screen label)'),
  shortName: z.string().min(1).max(12).describe('Short name (Android home screen)'),
  description: z.string().max(200).describe('PWA manifest description'),
  themeColor: HexColourSchema.describe('Browser chrome / status bar colour'),
  backgroundColor: HexColourSchema.describe('Splash background colour'),
  startUrl: z.string().default('/').describe('PWA start URL'),
  scope: z.string().default('/').describe('PWA scope'),
  display: z.enum(['standalone', 'fullscreen', 'minimal-ui']).default('standalone'),
  orientation: z.enum(['portrait', 'landscape', 'any']).default('portrait'),
  lang: LocaleSchema.default('en'),
});
export type AppIdentity = z.infer<typeof AppIdentitySchema>;

export const IconEntrySchema = z.object({
  src: z.string().describe('Path served by /icons/v{N}/...'),
  size: z.number().int().positive(),
  purpose: z.enum(['any', 'maskable', 'monochrome', 'apple-touch']),
  mime: z.literal('image/png').default('image/png'),
});
export type IconEntry = z.infer<typeof IconEntrySchema>;

/**
 * iOS apple-touch-startup-image entry (Sprint 31).
 *
 * iOS Safari doesn't honour the PWA manifest splash; it needs explicit
 * `<link rel="apple-touch-startup-image">` tags per device size + media query.
 * `brandingRegenerate` pre-renders one PNG per supported device into
 * `tenants/{tid}/apple-startup/v{N}/{key}.png` and writes the entries here;
 * the customer-pwa `index.html` carries static `<link>` tags pointing to
 * `/apple-startup/v0/{key}.png` and the `tenantSplash` Cloud Function
 * 308-redirects to the current version (mirrors the icon pipeline).
 *
 * `mediaQuery` is the operator-Safari-recognised CSS media query that pairs
 * the splash image with the right device viewport + DPR. We always emit
 * portrait orientation for MVP; landscape variants are a Phase 2 polish.
 */
export const SplashEntrySchema = z.object({
  key: z.string().describe('Filename stem (without .png), e.g. "iphone-11"'),
  width: z.number().int().positive().describe('Native pixel width'),
  height: z.number().int().positive().describe('Native pixel height'),
  mediaQuery: z.string().describe('Apple Safari media query'),
});
export type SplashEntry = z.infer<typeof SplashEntrySchema>;

export const ColoursSchema = z.object({
  primary: HexColourSchema,
  primaryFg: HexColourSchema,
  secondary: HexColourSchema,
  secondaryFg: HexColourSchema,
  accent: HexColourSchema,
  accentFg: HexColourSchema,
  surface: HexColourSchema,
  surfaceFg: HexColourSchema,
});
export type Colours = z.infer<typeof ColoursSchema>;

export const TypographySchema = z.object({
  font: z.string().describe('"system" or a Google Font family name'),
  weight: z.enum(['regular', 'medium', 'semibold']).default('regular'),
});
export type Typography = z.infer<typeof TypographySchema>;

export const HeroSchema = z.object({
  imageUrl: z.string().url().optional(),
  headline: I18nStringSchema,
  subheadline: I18nStringSchema.optional(),
});
export type Hero = z.infer<typeof HeroSchema>;

export const CopyBlockSchema = z.object({
  tagline: I18nStringSchema,
  about: I18nStringSchema,
  welcomeNewCustomer: I18nStringSchema,
  thankYouCollected: I18nStringSchema,
  receiptFooter: I18nStringSchema,
});
export type CopyBlock = z.infer<typeof CopyBlockSchema>;

export const ContactSchema = z.object({
  phone: z.string().optional(),
  whatsapp: z.string().optional(),
  email: z.string().email().optional(),
  addressLine: z.string().optional(),
  mapsLink: z.string().url().optional(),
  instagram: z.string().url().optional(),
  facebook: z.string().url().optional(),
  tiktok: z.string().url().optional(),
  website: z.string().url().optional(),
  /**
   * The operator's "write a review" link on Google (Share & grow). The customer
   * app offers it after EVERY in-app rating, whatever the stars — Google's
   * contribution policy forbids asking only happy customers ("review gating")
   * and forbids rewarding reviews, so neither is ever done.
   */
  googleReview: z.string().url().optional(),
});
export type Contact = z.infer<typeof ContactSchema>;

export const HoursDaySchema = z.object({
  isOpen: z.boolean(),
  open: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  close: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
});
export const WeeklyHoursSchema = z.object({
  sun: HoursDaySchema,
  mon: HoursDaySchema,
  tue: HoursDaySchema,
  wed: HoursDaySchema,
  thu: HoursDaySchema,
  fri: HoursDaySchema,
  sat: HoursDaySchema,
});
export const HoursSchema = z.object({
  weekly: WeeklyHoursSchema,
  holidayOverrides: z
    .array(
      z.object({
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        isOpen: z.boolean(),
        open: z
          .string()
          .regex(/^\d{2}:\d{2}$/)
          .optional(),
        close: z
          .string()
          .regex(/^\d{2}:\d{2}$/)
          .optional(),
        label: z.string().optional(),
      }),
    )
    .default([]),
  temporaryClosure: z
    .object({
      from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      messageI18n: I18nStringSchema,
    })
    .optional(),
});
export type Hours = z.infer<typeof HoursSchema>;

export const PhotoSchema = z.object({
  url: z.string().url(),
  caption: I18nStringSchema.optional(),
});

export const BrandingSchema = z.object({
  app: AppIdentitySchema,
  logoUrl: z
    .string()
    .url()
    .optional()
    .describe(
      "Source-of-truth uploaded logo, used to regenerate the icon set; absent for seed-state tenants who haven't uploaded a logo yet",
    ),
  iconSet: z.array(IconEntrySchema).default([]),
  splashSet: z
    .array(SplashEntrySchema)
    .default([])
    .describe('iOS apple-touch-startup-image set, generated by brandingRegenerate (Sprint 31)'),
  colours: ColoursSchema,
  typography: TypographySchema,
  hero: HeroSchema,
  copy: CopyBlockSchema,
  contact: ContactSchema,
  hours: HoursSchema,
  photos: z.array(PhotoSchema).max(12).default([]),
  brandingVersion: z
    .number()
    .int()
    .nonnegative()
    .describe('Monotonic int bumped on every save; used in manifest/icon ETags'),
  customDomain: z.string().optional().describe('Phase 2'),
  listedInFindDirectory: z
    .boolean()
    .default(false)
    .describe('Opt-in to listing on find.kilatcarwash.net'),
});
export type Branding = z.infer<typeof BrandingSchema>;
